import { useContext, useEffect, useState } from 'react'

import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, Stack, rem } from '@mantine/core'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { Container } from 'components/atoms/container'
import Warning from 'components/atoms/icons/Warning'
import { AdBlock } from 'components/molecules/adBlock'
import { AdBlockContainer } from 'components/molecules/adBlock/adBlockContainer'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/deferFixedBottomAdBlock'
import { BmrForm, BmrFormHelpers } from 'components/molecules/bmrForm'
import { FaqAccordion } from 'components/molecules/faqAccordion'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import { ToolsBannerCardV2 } from 'components/molecules/toolsBannerCardV2'
import { ToolsInfoCard } from 'components/molecules/toolsInfoCard'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { HealthToolLayout } from 'components/templates/healthToolLayout'
import {
  NavigatorLayout,
  getDefaultLanguageOptions,
} from 'components/templates/navigatorLayout'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import { HEALTH_TOOL_NAME } from 'interfaces/constants/healthTools'
import { FooterInfo, ToolContent } from 'interfaces/types'
import { AppContext } from 'state/appContext'
// import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'

import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'

import {
  BMR_SIDIS_KEY,
  generateBmrInfoToSendSidis,
  generateBmrRedirect,
} from './helpers'
import { HEALTH_TOOL_COMMON_CONFIGS, ToolPageType } from '../helpers'
import {
  StyledHealthTools,
  StyledSuggestedArticlesContainer,
} from '../index.styled'

export type InputError = {
  age: string
  weight: string
  height: string
}

export type Props = {
  type: ToolPageType
  isMobileSsr: boolean
  bmrContent: ToolContent
  bmrContentWParams: ToolContent
  pageType?: string
  footerInfo?: FooterInfo
  dataNavMenuContent?: NavMenuContentType
}

const BMRToolPage = ({
  bmrContent,
  bmrContentWParams,
  footerInfo,
  dataNavMenuContent,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const { router, asPath } = useRouterExtend()

  const isMobile = useIsMobile()
  const {
    state: { isLoggedIn },
  } = useContext(AppContext) || {}
  const { locale } = useRouterExtend()

  const [, setIsRecalculate] = useState<boolean>(false)

  const onSubmit = async (info: BmrFormHelpers.BmrFormValue) => {
    const bmrInfo = generateBmrInfoToSendSidis(
      info.bmr || 0,
      String(info.gender),
      info as any,
      {
        isMobile,
        positionType: 'landing_page',
      },
    )

    localStorage.setItem(BMR_SIDIS_KEY, JSON.stringify(bmrInfo))
    ;(async () => {
      const currentOrigin = window.location.origin
      const currentPathname = window.location.pathname
      const destination = generateBmrRedirect(
        currentOrigin + currentPathname,
        // info.bmr || 0,
        String(info.gender),
        info as any,
      )
      if (!isLoggedIn) {
        redirectToSSOWithReturnUrl(destination, 'bmr')
      } else {
        router.push(destination)
      }
    })()
  }

  const bmrInfo = {
    title: f({
      id: 'tools.info',
      defaultMessage: 'Info',
    }),
    items: bmrContent.information,
    // [
    //   {
    //     title: f({
    //       id: 'bmr.info.definition.title',
    //       defaultMessage: 'What is BMR? – BMR definition',
    //     }),
    //     description: f({
    //       id: 'bmr.info.definition.text',
    //       defaultMessage: 'What is BMR? – BMR definition',
    //     }),
    //   },
    //   {
    //     title: f({
    //       id: 'bmr.info.calories.title',
    //       defaultMessage: 'What are Calories and What do We Need Them For?',
    //     }),
    //     description: f({
    //       id: 'bmr.info.calories.text',
    //       defaultMessage: 'What are Calories and What do We Need Them For?',
    //     }),
    //   },
    // ],
  }

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, asPath, ['health-tools/bmr']),
  })

  useEffect(() => {
    if (window?.location?.hash === '#recalculate') {
      setIsRecalculate(true)
    }
  }, [])

  const pageItem = [
    {
      label: f({ id: 'healthtools.title' }),
      value: `/health-tools`,
    },
    {
      label: f({
        id: 'bmr.heading',
        defaultMessage: 'BMR Calculator',
      }),
      value: '#',
    },
  ]

  const enabledAd = !!bmrContent?.enable_ad

  return (
    <>
      <NavigatorLayout
        title={f({ id: 'bmrPage.seoTitle', defaultMessage: 'BMR Tool' })}
        description={f({
          id: 'bmrPage.seoDescription',
          defaultMessage: 'BMR Tool',
        })}
        image={`${getHelloSitesUrl(
          locale,
        )}${assetImagePath}/bmrThumbnail.png?v=1`}
        SEOData={{
          schemaData: f({
            id: 'bmrPage.seoSchema',
            defaultMessage: 'BMR Tool',
          }),
          apiMetaData: bmrContentWParams?.metaTag || bmrContent?.metaTag,
          isIndexable: true,
        }}
        footerInfo={footerInfo}
        sponsoredType="content"
        sponsoredBy={bmrContent?.sponsor || undefined}
        isSponsored={bmrContent.is_sponsored}
        dataNavMenuContent={dataNavMenuContent}
        languageOptions={getDefaultLanguageOptions(locale, router.asPath)}
        activeMenuService="tools"
        isHealthToolResultSharing
      >
        <StyledHealthTools>
          <Box
            sx={(theme) => ({
              backgroundColor:
                theme.colors[
                  HEALTH_TOOL_COMMON_CONFIGS[HEALTH_TOOL_NAME.BMR]
                ][0],
              paddingBottom: 24,
            })}
          >
            <ToolsBannerCardV2
              breadcrumbProps={{ pageItem }}
              icon={
                <img
                  alt="health-tool-icon"
                  loading="lazy"
                  src={bmrContent.featured_image || `${assetImagePath}/bmr.svg`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              }
              heading={
                bmrContent.title ||
                f({
                  id: 'bmr.heading',
                  defaultMessage: 'BMI Calculator',
                })
              }
              description={
                bmrContent.description ||
                f({
                  id: 'bmr.description',
                  defaultMessage: 'BMI Calculator',
                })
              }
              reviewBy={bmrContent.reviewBy}
              isMobile={isMobile}
              styles={(theme) => ({
                root: { backgroundColor: 'transparent' },
                inner: {
                  [theme.fn.largerThan('sm')]: {
                    paddingBottom: 8,
                  },
                },
                thumbnailWrapper: {
                  width: 32,
                },
                thumbnailInner: {
                  [theme.fn.largerThan('sm')]: {
                    width: 66,
                    height: 66,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  },
                  borderRadius: '100%',
                  boxShadow: '0px 3.9px 19.51px 0px #00B16A3D',
                },
                thumbnailCol: {
                  [theme.fn.largerThan('sm')]: {
                    right: 24,
                  },
                },
                thumbnail: {
                  [theme.fn.smallerThan('sm')]: {
                    display: 'none',
                  },
                  [theme.fn.largerThan('sm')]: {
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '100%',
                    boxShadow: '0px 3.9px 19.51px 0px #00B16A3D',
                  },
                },
                medicalReview: {
                  [theme.fn.smallerThan('sm')]: {
                    paddingRight: 0,
                  },
                },
              })}
            />
            <Container>
              <Box
                sx={(theme) => ({
                  [theme.fn.largerThan('sm')]: {
                    paddingLeft: 24,
                    paddingRight: 24,
                  },
                })}
              >
                <BmrForm onSubmit={onSubmit as any} template="page" />
              </Box>
            </Container>
          </Box>
          <HealthToolLayout>
            <HealthToolLayout.LeftColumn>
              {enabledAd && isMobile && (
                <AdBlock
                  adUnitPath={adUnitPath}
                  className="top-ad"
                  size={[300, 250]}
                  divId={'div-gpt-ad-topbanner-mobile'}
                  pos={['TopBanner']}
                />
              )}
              {bmrContent.disclaimer && (
                <ToolsInfoCard
                  headingTitle={f({ id: 'tools.disclaimer' })}
                  icon={<Warning size={18} />}
                >
                  <Stack spacing={rem(16)}>
                    <Text size="p2">{bmrContent.disclaimer}</Text>
                  </Stack>
                </ToolsInfoCard>
              )}
              {bmrInfo.items && bmrInfo.items.length > 0 && (
                <FaqAccordion
                  faqs={bmrInfo.items}
                  titleProps={{
                    icon: (
                      <img
                        style={{
                          height: '16px',
                          width: '16px',
                        }}
                        src={`${assetImagePath}/info-icon.svg`}
                        alt=""
                        loading="lazy"
                      />
                    ),
                    headingTitle: f({
                      id: 'tools.info',
                      defaultMessage: 'Info',
                    }),
                  }}
                />
              )}
              {isMobile && <div className="top-ad"></div>}
            </HealthToolLayout.LeftColumn>
            <HealthToolLayout.RightColumn>
              {enabledAd && !isMobile && (
                <StyledSuggestedArticlesContainer>
                  <div className="top-ad">
                    <AdBlock
                      adUnitPath={adUnitPath}
                      size={[
                        [300, 250],
                        [300, 600],
                      ]}
                      divId={'div-gpt-ad-topbanner-desktop'}
                      pos={['TopBanner']}
                    />
                  </div>
                  <div className="bottom-ad"></div>
                </StyledSuggestedArticlesContainer>
              )}
            </HealthToolLayout.RightColumn>
          </HealthToolLayout>
          {enabledAd && (
            <AdBlockContainer>
              <Container>
                <FixedBottomAdBlock
                  adUnitPath={adUnitPath}
                  className="article-bottom-ad"
                  isMobile={isMobile}
                />
              </Container>
            </AdBlockContainer>
          )}
          {isMobile && <HealthToolBreadcrumb pageItem={pageItem} />}
        </StyledHealthTools>
      </NavigatorLayout>
      <MobileBottomNavigationWrapper.FitnessHealthTool />
    </>
  )
}

export default BMRToolPage
